import styled, { createGlobalStyle, keyframes } from 'styled-components'

const c = {
  bg: '#f4f6f8',
  card: '#ffffff',
  border: '#e3e7ec',
  border2: '#d3d9e0',
  txt: '#1f2733',
  txt2: '#5b6675',
  txt3: '#9aa4b2',
  accent: '#2563eb',
  ok: '#16a34a',
  okBg: '#e9f7ef',
  err: '#dc2626',
  errBg: '#fdecec',
  codeBg: '#0f172a',
  codeTxt: '#e2e8f0',
  sans: "-apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  mono: "'Roboto Mono', 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to   { opacity: 1; transform: translateY(0); }
`

export const GlobalStyles = createGlobalStyle``

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  min-height:100vh; padding:22px 26px 60px; box-sizing:border-box;
`

export const Header = styled.div`
  margin-bottom:18px;
  h1 { font-size:22px; font-weight:700; margin:0; }
  p  { font-size:13px; color:${c.txt2}; margin:4px 0 0; }
`

export const FilterBar = styled.div`
  background:${c.card}; border:1px solid ${c.border}; border-radius:12px;
  padding:14px 16px; display:flex; flex-wrap:wrap; align-items:flex-end; gap:12px;
  box-shadow:0 1px 2px rgba(16,24,40,.04);
`
export const Field = styled.label`
  display:flex; flex-direction:column; gap:5px;
  font-size:11px; color:${c.txt2}; font-weight:600;
  text-transform:uppercase; letter-spacing:.03em;
`
export const Input = styled.input`
  background:#fff; border:1px solid ${c.border2}; border-radius:8px;
  color:${c.txt}; padding:8px 11px; font-size:13px; font-family:${c.sans};
  outline:none; min-width:150px;
  &:focus { border-color:${c.accent}; box-shadow:0 0 0 3px ${c.accent}22; }
  &::placeholder { color:${c.txt3}; }
`
export const Select = styled.select`
  background:#fff; border:1px solid ${c.border2}; border-radius:8px;
  color:${c.txt}; padding:8px 11px; font-size:13px; font-family:${c.sans};
  outline:none; cursor:pointer;
  &:focus { border-color:${c.accent}; box-shadow:0 0 0 3px ${c.accent}22; }
`
export const Actions = styled.div`
  display:flex; gap:8px; margin-left:auto;
`
export const Btn = styled.button`
  padding:9px 18px; border-radius:8px; cursor:pointer;
  font-size:13px; font-weight:600; font-family:${c.sans}; transition:.12s;
  background:${({ $primary }) => ($primary ? c.accent : '#fff')};
  border:1px solid ${({ $primary }) => ($primary ? c.accent : c.border2)};
  color:${({ $primary }) => ($primary ? '#fff' : c.txt2)};
  &:hover { ${({ $primary }) => ($primary ? 'filter:brightness(1.07);' : `border-color:${c.accent}; color:${c.accent};`)} }
  &:disabled { opacity:.55; cursor:not-allowed; }
`

export const ResultsBar = styled.div`
  display:flex; align-items:center; gap:12px; margin:18px 2px 10px;
  font-size:13px; color:${c.txt2};
  strong { color:${c.txt}; }
  .right { margin-left:auto; display:flex; align-items:center; gap:8px; font-size:12px; }
`

export const List = styled.div`
  background:${c.card}; border:1px solid ${c.border}; border-radius:12px;
  overflow:hidden; box-shadow:0 1px 2px rgba(16,24,40,.04);
`
const cols = 'grid-template-columns: 34px 170px minmax(120px,1.2fr) 120px 96px 2fr;'
export const ListHead = styled.div`
  display:grid; ${cols}
  gap:10px; padding:11px 16px; background:#fafbfc; border-bottom:1px solid ${c.border};
  font-size:10px; font-weight:700; color:${c.txt3};
  text-transform:uppercase; letter-spacing:.05em;
  @media (max-width: 860px) { display:none; }
`

export const LogItem = styled.div`
  border-bottom:1px solid ${c.border};
  animation:${fadeIn} .15s ease;
  &:last-child { border-bottom:none; }
`
export const LogHeader = styled.div`
  display:grid; ${cols}
  gap:10px; align-items:center; padding:11px 16px; cursor:pointer;
  font-size:13px; transition:background .1s;
  &:hover { background:#f8fafc; }

  .chevron {
    width:22px; height:22px; border-radius:6px; display:inline-flex;
    align-items:center; justify-content:center; font-size:15px; font-weight:700;
    background:${({ $open }) => ($open ? c.accent : '#eef1f5')};
    color:${({ $open }) => ($open ? '#fff' : c.txt2)};
  }
  .dt   { font-family:${c.mono}; font-size:12px; color:${c.txt2}; }
  .order{ font-family:${c.mono}; font-weight:600; }
  .idorder { font-family:${c.mono}; color:${c.txt2}; }
  .msg  { color:${c.txt2}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

  @media (max-width: 860px) {
    grid-template-columns: 34px 1fr; row-gap:4px;
    .dt, .idorder, .msg { grid-column:2; }
  }
`
export const StatusBadge = styled.span`
  display:inline-flex; align-items:center; gap:5px; justify-self:start;
  padding:3px 10px; border-radius:11px; font-size:11px; font-weight:700;
  text-transform:capitalize;
  background:${({ $status }) => ($status === 'erro' ? c.errBg : c.okBg)};
  color:${({ $status }) => ($status === 'erro' ? c.err : c.ok)};
  &::before {
    content:''; width:6px; height:6px; border-radius:50%;
    background:${({ $status }) => ($status === 'erro' ? c.err : c.ok)};
  }
`
export const LogBody = styled.div`
  padding:6px 16px 18px 50px; background:#fcfdfe;
  .meta { font-size:12px; color:${c.txt2}; margin:6px 0 10px; }
  .meta b { color:${c.txt}; }
  .jsonlabel {
    font-size:10px; font-weight:700; color:${c.txt3};
    text-transform:uppercase; letter-spacing:.05em; margin-bottom:6px;
  }
`
export const Json = styled.pre`
  margin:0; background:${c.codeBg}; color:${c.codeTxt};
  border-radius:10px; padding:14px 16px; overflow:auto; max-height:420px;
  font-family:${c.mono}; font-size:12px; line-height:1.55;
  white-space:pre; word-break:normal;
  &::-webkit-scrollbar { width:8px; height:8px; }
  &::-webkit-scrollbar-thumb { background:#334155; border-radius:4px; }
`

// ─── Reenvio da data do evento ───────────────────────────────────────────────
// Escrita DIRETA no ERP, sem passar pelo cálculo — por isso fica dentro do
// evento expandido, com a data à vista e confirmação antes de disparar.
export const ResendBar = styled.div`
  display:flex; align-items:center; gap:14px; flex-wrap:wrap;
  margin:0 0 14px; padding:11px 14px;
  background:#fffaf0; border:1px solid #fcd9a4; border-radius:10px;
  .info { font-size:12.5px; color:${c.txt2}; }
  .info strong {
    font-family:${c.mono}; font-size:13px; color:${c.txt}; margin-left:6px;
  }
  .spacer { margin-left:auto; }
`

export const ConfirmOverlay = styled.div`
  position:fixed; inset:0; z-index:2400;
  background:rgba(15,23,42,.55); backdrop-filter:blur(3px);
  display:flex; align-items:center; justify-content:center; padding:20px;
`
export const ConfirmBox = styled.div`
  background:${c.card}; border-radius:14px; padding:24px 26px;
  width:520px; max-width:100%;
  box-shadow:0 24px 64px rgba(0,0,0,.3);
  font-family:${c.sans};

  h3 { margin:0 0 10px; font-size:17px; color:${c.txt}; }
  p { margin:0 0 6px; font-size:13.5px; color:${c.txt2}; line-height:1.55; }

  .change {
    display:flex; align-items:center; gap:10px; flex-wrap:wrap;
    margin:14px 0 4px; padding:12px 14px;
    background:#f8fafc; border:1px solid ${c.border}; border-radius:10px;
    font-family:${c.mono}; font-size:13px; color:${c.txt};
  }
  .change .arrow { color:${c.txt3}; }
  .change .to { font-weight:700; }

  .warn {
    margin-top:12px; font-size:12.5px; color:#b45309;
    background:#fffaf0; border:1px solid #fcd9a4;
    border-radius:8px; padding:9px 12px;
  }

  .actions { display:flex; justify-content:flex-end; gap:8px; margin-top:20px; }
`

export const Pagination = styled.div`
  display:flex; align-items:center; justify-content:center; gap:14px;
  margin-top:18px; font-size:13px; color:${c.txt2};
`
export const PageBtn = styled.button`
  padding:7px 14px; border-radius:8px; cursor:pointer;
  background:#fff; border:1px solid ${c.border2}; color:${c.txt2};
  font-size:13px; font-weight:600; transition:.12s;
  &:hover:not(:disabled) { border-color:${c.accent}; color:${c.accent}; }
  &:disabled { opacity:.45; cursor:not-allowed; }
`

export const Empty = styled.div`
  text-align:center; padding:50px 20px; color:${c.txt3}; font-size:14px;
`
export const Loading = styled.div`
  text-align:center; padding:44px 20px; color:${c.txt3}; font-size:13px;
`
